import ClearIcon from '@/assets/images/icons/clear-icon.svg';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Platform, Pressable } from 'react-native';
import { XStack, YStack } from 'tamagui';
import { BodyText, CardTitle } from '@/components/ui/Typography';

export type FlashStyleType = 'success' | 'error' | 'info';

const PALETTE: Record<
  FlashStyleType,
  { bg: string; border: string; text: string; icon: keyof typeof Ionicons.glyphMap }
> = {
  success: {
    bg: '#E8F5E9',
    border: '#4CAF50',
    text: '#2E7D32',
    icon: 'checkmark-circle',
  },
  error: {
    bg: '#FFEBEE',
    border: '#E57373',
    text: '#C62828',
    icon: 'close-circle',
  },
  info: {
    bg: '#E3F2FD',
    border: '#2196F3',
    text: '#1565C0',
    icon: 'information-circle',
  },
};

export type FlashStyleTopToastProps = {
  type: FlashStyleType;
  title: string;
  message: string;
  onClose: () => void;
};

const FLASH_SHADOW =
  Platform.OS === 'web'
    ? {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.07,
        shadowRadius: 4,
        elevation: 0 as const,
      }
    : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 2,
        elevation: 0 as const,
      };

export function FlashStyleTopToast({ type, title, message, onClose }: FlashStyleTopToastProps) {
  const p = PALETTE[type];
  return (
    <YStack
      backgroundColor={p.bg}
      borderColor={p.border}
      borderWidth={1}
      borderRadius={12}
      paddingVertical={12}
      paddingHorizontal={14}
      width="100%"
      maxWidth={400}
      {...FLASH_SHADOW}
    >
      <XStack alignItems="center" justifyContent="space-between" marginBottom={8}>
        <XStack alignItems="flex-start" flex={1} gap={6}>
          <Ionicons name={p.icon} size={20} color={p.text} />
          <CardTitle color={p.text} flexShrink={1}>
            {title}
          </CardTitle>
        </XStack>
        <Pressable onPress={onClose} hitSlop={8} accessibilityRole="button" accessibilityLabel="Dismiss">
          <ClearIcon width={18} height={18} />
        </Pressable>
      </XStack>
      <BodyText color={p.text}>
        {message}
      </BodyText>
    </YStack>
  );
}
